<template>
    <dialog
        ref="dialogEl"
        class="ds-sheet"
        @cancel.prevent="onCancel"
        @click="onBackdropClick"
        @close="closeSheet"
        @keydown.esc.prevent="closeSheet"
    >
        <div
            ref="dialogContentEl"
            class="dialog-content"
            :style="{ '--sheet-width': `${SHEET_SIZES[size]}px` }"
        >
            <button
                :aria-label="`Dismiss ${title}`"
                class="sheet-handle"
                type="button"
                @click="closeSheet"
            />
            <div class="sheet-header">
                <h2 class="sheet-title">
                    {{ title }}
                </h2>
                <DsIconButton
                    :aria-label="`Close ${title}`"
                    icon="fa-solid fa-times"
                    size="sm"
                    tooltipText=""
                    @click="closeSheet"
                />
            </div>

            <div
                ref="sheetBodyEl"
                class="sheet-body"
            >
                <slot />
            </div>

            <div
                v-if="hasFooter"
                class="sheet-footer"
            >
                <slot name="footer">
                    <DsButton
                        v-if="secondaryAction"
                        class="sheet-footer-button"
                        :loading="secondaryLoading"
                        size="md"
                        variant="secondary"
                        @click="$emit('clickSecondary')"
                    >
                        {{ secondaryAction }}
                    </DsButton>
                    <DsButton
                        class="sheet-footer-button"
                        :loading="primaryLoading"
                        size="md"
                        variant="primary"
                        @click="$emit('clickPrimary')"
                    >
                        {{ primaryAction }}
                    </DsButton>
                </slot>
            </div>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import { isMobile } from '@/components/Composables/MobileScreenDetection';
import DsIconButton from '@ds/DsIconButton.vue';
import DsButton from '@ds/form/input/DsButton.vue';
import { useScrollLock, useSwipe } from '@vueuse/core';
import { computed, useSlots, useTemplateRef, watch } from 'vue';

type DsSheetSize = 'sm' | 'md';

interface DsSheetProps {
    title: string;
    size?: DsSheetSize;
    description?: string;
    primaryAction?: string;
    primaryLoading?: boolean;
    secondaryLoading?: boolean;
    secondaryAction?: string;
    closeOnBackdropClick?: boolean;
    cancelable?: boolean;
}

const open = defineModel<boolean>('open');
const props = withDefaults(defineProps<DsSheetProps>(), {
    closeOnBackdropClick: true,
    size: 'sm',
    cancelable: true,
});

defineEmits<{
    clickPrimary: [];
    clickSecondary: [];
}>();

const dialogEl = useTemplateRef('dialogEl');
const dialogContentEl = useTemplateRef('dialogContentEl');
const sheetBodyEl = useTemplateRef('sheetBodyEl');
const isScrollLocked = useScrollLock(document.body);

useSwipe(dialogContentEl, {
    onSwipeEnd(_event, direction) {
        if (isMobile.value && direction === 'down' && sheetBodyEl.value?.scrollTop === 0) {
            closeSheet();
        }
    },
});

const closeSheet = () => {
    open.value = false;
};

const handleClose = () => {
    if (!dialogEl.value) return;
    dialogEl.value.classList.add('closing');
    dialogEl.value.addEventListener(
        'animationend',
        () => {
            dialogEl.value?.classList.remove('closing');
            dialogEl.value?.close();
        },
        { once: true },
    );
};

watch(open, isOpen => {
    if (!dialogEl.value) return;

    isScrollLocked.value = !!isOpen;

    if (isOpen) {
        dialogEl.value?.showModal();
    } else {
        handleClose();
    }
});

const onBackdropClick = (event: MouseEvent) => {
    if (props.closeOnBackdropClick && event.target === dialogEl.value) {
        closeSheet();
    }
};

/** Fired when focus is lost. Other native interactions like closing
 * a native file picker can fire this event.
 */
const onCancel = () => {
    if (props.cancelable) {
        closeSheet();
    }
};

const hasFooter = computed(() => {
    return !!(props.primaryAction || props.secondaryAction || useSlots().footer);
});

const SHEET_SIZES: Record<DsSheetSize, number> = {
    sm: 400,
    md: 600,
};
</script>

<style scoped lang="scss">
@import '$sass/dialog-fading.scss';
@import '$sass/breakpoints.scss';

.ds-sheet {
    --duration: 300ms;
    margin-left: auto !important; /* Necessary for positioning in Client Portal */
    @apply bg-transparent outline-none;

    &,
    &::backdrop {
        animation-duration: var(--duration);
        animation-fill-mode: forwards;
    }

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.7);
    }

    &[open]::backdrop {
        animation-name: open-dialog;
        animation-timing-function: ease-out;
    }

    &.closing::backdrop {
        animation-name: close-dialog;
        animation-timing-function: ease-in;
    }

    .dialog-content {
        @apply flex max-h-dvh min-h-[50dvh] flex-col rounded-b-none rounded-t-3xl bg-white;
    }

    &[open],
    &.closing {
        @apply m-0 h-full max-h-full p-0;

        .dialog-content {
            @apply fixed bottom-0 left-0 right-0 mx-auto max-w-[600px];
        }
    }

    &[open] .dialog-content {
        animation: slide-in-bottom var(--duration) ease-out forwards;
    }

    &.closing .dialog-content {
        animation: slide-out-bottom var(--duration) ease-in forwards;
    }

    .sheet-handle {
        @apply mx-auto block w-full appearance-none border-none bg-transparent py-2;

        &::before {
            @apply mx-auto block h-1 w-8 rounded-full bg-ds-primary-300 content-[''];
        }
    }

    .sheet-header {
        @apply flex flex-shrink-0 items-center justify-between px-4 py-1;
    }

    .sheet-title {
        @apply text-content-lg font-semibold text-content-primary;
    }

    .sheet-body {
        scrollbar-gutter: stable;
        @apply grow overflow-auto p-6;
    }

    .sheet-footer {
        @apply flex flex-shrink-0 flex-col-reverse gap-3 border-t border-ds-primary-200 px-4 py-2;
    }

    .sheet-footer-button {
        @apply w-full;
    }

    @media screen and (min-width: $DS_BREAKPOINT_MD) {
        &[open],
        &.closing {
            @apply p-2;

            .dialog-content {
                width: var(--sheet-width, 400px);
                @apply static mx-0 h-full animate-none rounded-3xl;
            }
        }

        &[open] {
            animation: slide-in var(--duration) ease-out forwards;
        }

        &.closing {
            animation: slide-out var(--duration) ease-in forwards;
        }

        .sheet-handle {
            @apply hidden;
        }

        .sheet-header {
            @apply border-b border-ds-primary-200 px-6 py-4;
        }

        .sheet-footer {
            @apply flex-row items-center justify-end px-6 py-4;
        }

        .sheet-footer-button {
            @apply w-auto;
        }
    }
}
</style>
